import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { useNavigationSettings } from '@/hooks/useNavigationSettings';
import { NAVIGATION_ITEMS, NavigationSettings as NavigationSettingsType } from '@/types/navigationTypes';
import { AdminLoadingState } from '@/components/admin/AdminStates';
import { Menu, Eye, EyeOff, Settings, Users } from 'lucide-react';

const NavigationSettings: React.FC = () => {
  const {
    navigationSettings,
    canManage,
    isLoading,
    isCheckingPermissions,
    isUpdating,
    updateNavigationSettings,
  } = useNavigationSettings();

  const [localSettings, setLocalSettings] = useState<NavigationSettingsType>(navigationSettings);
  const [hasChanges, setHasChanges] = useState(false);

  // Update local settings when server data changes
  useEffect(() => {
    setLocalSettings(navigationSettings);
    setHasChanges(false);
  }, [navigationSettings]);

  // Check for changes
  useEffect(() => {
    const changed = JSON.stringify(localSettings) !== JSON.stringify(navigationSettings);
    setHasChanges(changed);
  }, [localSettings, navigationSettings]);

  const handleToggle = (key: keyof NavigationSettingsType) => {
    if (!canManage) return;
    
    setLocalSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSave = () => {
    updateNavigationSettings(localSettings);
  };

  const handleReset = () => {
    setLocalSettings(navigationSettings);
  };

  // Group items by category
  const groupedItems = NAVIGATION_ITEMS.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {} as Record<string, typeof NAVIGATION_ITEMS>);

  const categoryTitles = {
    management: 'Admin Management',
    financial: 'Financial',
    communication: 'Communication',
    data: 'Data & Content',
    other: 'Other Features'
  };

  if (isLoading || isCheckingPermissions) {
    return <AdminLoadingState message="Loading navigation settings..." />;
  }

  if (!canManage) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Menu className="h-5 w-5 text-muted-foreground" />
            <CardTitle>Navigation Settings</CardTitle>
          </div>
          <CardDescription>
            You don't have permission to manage navigation settings. Contact your administrator for access.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Users className="h-4 w-4" />
            <span className="text-sm">Only business administrators can customize the navigation menu.</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Menu className="h-5 w-5 text-primary" />
              <div>
                <CardTitle>Navigation Settings</CardTitle>
                <CardDescription>
                  Customize which menu items are visible to your team. Hidden items won't appear in the sidebar for any staff members.
                </CardDescription>
              </div>
            </div>
            <Badge variant="secondary" className="font-mono text-xs">
              {Object.values(localSettings).filter(Boolean).length} of {Object.keys(localSettings).length} enabled
            </Badge>
          </div>
        </CardHeader>
      </Card>

      {Object.entries(groupedItems).map(([category, items]) => (
        <Card key={category}>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{categoryTitles[category as keyof typeof categoryTitles]}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {items.map((item, index) => {
              const isEnabled = localSettings[item.key];
              return (
                <div key={item.key}>
                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-3">
                      {isEnabled ? (
                        <Eye className="h-4 w-4 text-green-500" />
                      ) : (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{item.title}</span>
                          {!isEnabled && (
                            <Badge variant="outline" className="text-xs">Hidden</Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {item.description}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={isEnabled}
                      onCheckedChange={() => handleToggle(item.key)}
                      disabled={isUpdating}
                    />
                  </div>
                  {index < items.length - 1 && <Separator />}
                </div>
              );
            })}
          </CardContent>
        </Card>
      ))}

      {hasChanges && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Settings className="h-4 w-4 text-orange-600" />
                <span className="text-sm font-medium text-orange-800">
                  You have unsaved changes
                </span>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleReset}
                  disabled={isUpdating}
                >
                  Reset
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={isUpdating}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  {isUpdating ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default NavigationSettings;